import { Col } from 'antd';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import StatusBox, { CustomColorT } from '~src/shared/StatusBox';
import CheckBox from '~src/shared/form/Checkbox';
import TileHeader from '~src/shared/headers/tileHeader/TileHeader';
import CollapsibleListItem from '~src/shared/lists/collapsibleListItem/CollapsibleListItem';

import {
  AttrLabel,
  AttrValue,
  EmailString,
  ErrorsList,
  Header,
  HeaderCell,
  PrevAttrValue,
  StatusWrapper,
  StyledRow,
  TableContainer,
} from './userPreviewItem.sc';
import {identity} from "lodash";

export interface UserPreviewAttrI {
  label: string[]; // [key, translation]
  value: string;
  prevValue?: string;
  error?: string;
}

export type UserPreviewStatus = 'new' | 'update' | 'activate' | 'deactivate';

export interface UserPreviewData {
  id: string; // user uuid
  integration_user_id: string;
  fullname: string; //
  email: string;
  username?: string;
  status: UserPreviewStatus;
  attributes: UserPreviewAttrI[] | null;
  errors?: string[][]; // [key, translation]
  isSelected?: boolean;
}

interface Props {
  userData: UserPreviewData;

  onToggleCheckbox(isChecked: boolean, value: string): void;
}

const UserPreviewItem: FC<Props> = (props) => {
  const [t] = useTranslation();
  const header = useMemo(() => {
    let statusProps: { customColor: CustomColorT; customKey: string } = {
      customColor: 'blue',
      customKey: 'new',
    };

    switch (true) {
      case !!props.userData.errors:
        statusProps = { customColor: 'red', customKey: 'error' };
        break;
      case props.userData.status === 'activate':
        statusProps = { customColor: 'blue', customKey: 'activate' };
        break;
      case props.userData.status === 'deactivate':
        statusProps = { customColor: 'yellow', customKey: 'deactivate' };
        break;
      case props.userData.status === 'update':
        statusProps = { customColor: 'green', customKey: 'updated' };
        break;
    }

    const displayName = [props.userData.email, props.userData.username].filter(identity).join(' / ');

    return (
      <Header>
        <CheckBox
          id={props.userData.id}
          val={props.userData.id}
          isChecked={!props.userData.errors && props.userData.isSelected}
          disabled={!!props.userData.errors}
          onChange={props.onToggleCheckbox}
          stopPropagation
        />
        <StatusWrapper>
          <StatusBox {...statusProps} />
        </StatusWrapper>
        <TileHeader
          title={
            <>
              {props.userData.fullname} <EmailString>{`(${displayName})`}</EmailString>
            </>
          }
          tooltip={'integration_user_id: ' + props.userData.integration_user_id}
        />
      </Header>
    );
  }, [
    props.userData.isSelected,
    props.userData.id,
    props.userData.fullname,
    props.userData.status,
    props.userData.errors,
  ]);

  return (
    <CollapsibleListItem marginTop={15} header={header}>
      <TableContainer>
        {props.userData.errors ? (
          <ErrorsList>
            {props.userData.errors.map(([key, txt]) => (
              <li key={key} data-translation={key}>
                {txt}
              </li>
            ))}
          </ErrorsList>
        ) : (
          <>
            <StyledRow gutter={15} type="flex">
              <Col span={6}>
                <HeaderCell data-translation="x.company.import.preview.attribute_name">
                  {t('x.company.import.preview.attribute_name')}
                </HeaderCell>
              </Col>
              <Col span={9}>
                <HeaderCell data-translation="x.company.import.preview.new_value_header">
                  {t('x.company.import.preview.new_value_header')}
                </HeaderCell>
              </Col>
              {props.userData.status === 'update' && (
                <Col span={9}>
                  <HeaderCell data-translation="x.company.import.preview.previous_value_header">
                    {t('x.company.import.preview.previous_value_header')}
                  </HeaderCell>
                </Col>
              )}
            </StyledRow>
            {props.userData.attributes &&
              props.userData.attributes.map((attr, i) => (
                <StyledRow gutter={15} type="flex" key={i}>
                  <Col span={6}>
                    <AttrLabel data-translation={attr.label[0]}>{attr.label[1]}</AttrLabel>
                  </Col>
                  <Col span={9}>
                    <AttrValue>{attr.value}</AttrValue>
                  </Col>
                  {attr.prevValue && (
                    <Col span={9}>
                      <PrevAttrValue>{attr.prevValue}</PrevAttrValue>
                    </Col>
                  )}
                </StyledRow>
              ))}
          </>
        )}
      </TableContainer>
    </CollapsibleListItem>
  );
};
export default UserPreviewItem;
